import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
    getSingleBill,
    clearSingleBill,
    clearBillState,
} from "../redux/slices/billSlice";
import moment from "moment";
import { getVendorBusinessProfile } from '../redux/slices/businessProfileSlice';

const BillInvoicePreview = () => {

    const { billId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { bill, loading, error } = useSelector((state) => state.bill);
    const { profile } = useSelector(
        (state) => state.businessProfile
    );
    const { user } = useSelector((state) => state.auth);
    const vendorId = user?.vendor?._id;

    /* ===============================
       FETCH BILL
    ================================ */
    useEffect(() => {
        dispatch(getSingleBill(billId));

        return () => {
            dispatch(clearSingleBill());
            dispatch(clearBillState());
        };
    }, [billId, dispatch]);

    useEffect(() => {
        if (vendorId) {
            dispatch(getVendorBusinessProfile(vendorId));
        }
    }, [vendorId, dispatch]);

    /* ===============================
       EDIT BILL
    ================================ */
    const handleEdit = (billId) => {
        Swal.fire({
            title: "Edit Bill?",
            text: "Do you want to update bill details?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, Edit",
        }).then((result) => {
            if (result.isConfirmed) {
                navigate(`/bill/edit/${billId}`);
            }
        });
    };

    /* ===============================
       PRINT BILL
    ================================ */
    const handlePrint = () => {
        Swal.fire({
            title: "Print Bill?",
            text: "Do you want to print this Bill/Invoice?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, Print",
        }).then((result) => {
            if (result.isConfirmed) {
                const printContents = document.getElementById("invoice").innerHTML;
                const printWindow = window.open("", "_blank");

                printWindow.document.write(`
                <html>
                    <head>
                        <title>Invoice</title>
                        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet"/>
                        <style>
                            body { padding: 20px; font-family: Arial, sans-serif; }
                            .table { width: 100%; }
                            .table th, .table td { padding: .5rem; }
                            .bordered-table td, .bordered-table th { border: 1px solid #dee2e6; }
                        </style>
                    </head>
                    <body>
                        ${printContents}
                    </body>
                </html>
            `);

                printWindow.document.close();
                printWindow.focus();
                printWindow.print();
                printWindow.close();
            }
        });
    };



    if (loading) return <p>Loading...</p>;
    if (error) return <p className="text-danger">{error}</p>;

    return (
        <div className="card">
            <div className="card-header">
                <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">

                    {bill && (
                        <Link
                            to="#"
                            onClick={() => handleEdit(bill._id)}
                            className="btn btn-sm btn-success radius-8 d-inline-flex align-items-center gap-1"
                        >
                            <Icon icon="uil:edit" className="text-xl" />
                            Edit
                        </Link>
                    )}

                    <button
                        type="button"
                        className="btn btn-sm btn-dark radius-8 d-inline-flex align-items-center gap-1"
                        onClick={handlePrint}
                    >
                        <Icon icon="basil:printer-outline" className="text-xl" />
                        Print
                    </button>
                    
                </div>
            </div>

            <div className="card-body py-40">
                <div className="row justify-content-center">
                    <div className="col-lg-8">
                        <div className="shadow-4 border radius-8">

                            <div className="p-20 d-flex flex-wrap justify-content-between gap-3 border-bottom">
                                <div>
                                    <h3 className="text-xl">
                                        Invoice #{bill?.invoiceNumber}
                                    </h3>
                                    <p className="mb-1 text-sm">
                                        Date Issued: {moment(bill?.createdAt).format("DD/MM/YYYY")}
                                    </p>
                                </div>
                                <div>
                                    <img src={profile?.logo?.url} alt="logo" className="mb-8" width={60} height={60} style={{ marginLeft: 200 }} />
                                    <h6>{profile?.businessName || "N/A"}</h6>
                                    <p className="mb-1 text-sm">
                                        {profile?.address}
                                    </p>
                                    <p className="mb-1 text-sm">
                                        GST : {profile?.gstNumber}
                                    </p>
                                    <p className="mb-0 text-sm">
                                        {profile?.email}, {profile?.contactNumber}
                                    </p>

                                </div>
                            </div>

                            <div className="py-28 px-20">
                                <div className="d-flex flex-wrap justify-content-between align-items-end gap-3">
                                    <div>
                                        <h6 className="text-md">Issus For:</h6>
                                        <table className="text-sm text-secondary-light">
                                            <tbody>
                                                <tr>
                                                    <td>Name</td>
                                                    <td className="ps-8">: {bill?.customerName}</td>
                                                </tr>
                                                <tr>
                                                    <td>Address</td>
                                                    <td className="ps-8">: {bill?.customerAddress}</td>
                                                </tr>
                                                <tr>
                                                    <td>Phone number</td>
                                                    <td className="ps-8">: {bill?.customerMobile}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div>
                                        <table className="text-sm text-secondary-light">
                                            <tbody>
                                                <tr>
                                                    <td>Issus Date</td>
                                                    <td className="ps-8">
                                                        : {moment(bill?.createdAt).format("DD/MM/YYYY")}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>Invoice No.</td>
                                                    <td className="ps-8">
                                                        : #{bill?.invoiceNumber}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* ITEMS TABLE */}
                                <div className="mt-24">
                                    <div className="table-responsive scroll-sm">
                                        <table className="table table-sm bordered-table text-sm">
                                            <thead>
                                                <tr>
                                                    <th>SL.</th>
                                                    <th>Items</th>
                                                    <th>Qty</th>
                                                    <th>Item Price</th>
                                                    <th className="text-end">Price</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bill?.items?.map((item, index) => (
                                                    <tr key={item._id}>
                                                        <td>{index + 1}</td>
                                                        <td>{item.name}</td>
                                                        <td>{item.quantity}</td>
                                                        <td>₹{item.price}</td>
                                                        <td className="text-end">
                                                            ₹{item.total}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* TOTALS */}
                                    <div className="d-flex flex-wrap justify-content-between gap-3">
                                        <div>
                                            <p className="text-sm mb-0">
                                                <span className="text-primary-light fw-semibold">
                                                    Payment Mode:
                                                </span>{" "}
                                                {bill?.paymentMode}
                                            </p>
                                            <p className="text-sm mb-0">
                                                Thanks for your business
                                            </p>
                                        </div>

                                        <div>
                                            <table className="text-sm">
                                                <tbody>
                                                    <tr>
                                                        <td className="pe-64">Subtotal:</td>
                                                        <td className="pe-16 text-end">
                                                            ₹{bill?.subTotal}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="pe-64">Tax:</td>
                                                        <td className="pe-16 text-end">
                                                            ₹{bill?.tax || 0}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="pe-64 pt-4 fw-bold">
                                                            Total:
                                                        </td>
                                                        <td className="pe-16 pt-4 text-end fw-bold">
                                                            ₹{bill?.grandTotal}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-64">
                                    <p className="text-center text-secondary-light text-sm fw-semibold">
                                        Thank you!
                                    </p>
                                </div>

                                <div className="d-flex flex-wrap justify-content-between align-items-end mt-64">
                                    <div className="text-sm border-top d-inline-block px-12">
                                        Signature of Customer
                                    </div>
                                    <div className="text-sm border-top d-inline-block px-12">
                                        Signature of Authorized
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div id="invoice" style={{ display: "none" }}>

                <table width="100%" cellPadding="0" cellSpacing="0" style={{ border: "1px solid #dee2e6" }}>
                    {/* ================= HEADER ================= */}
                    <tbody>
                        <tr>
                            <td style={{ padding: "20px", borderBottom: "1px solid #dee2e6" }}>
                                <table width="100%">
                                    <tbody>
                                        <tr>
                                            <td>
                                                <h3>Invoice #{bill?.invoiceNumber}</h3>
                                                <p style={{ margin: 0 }}>
                                                    Date Issued: {moment(bill?.createdAt).format("DD/MM/YYYY")}
                                                </p>
                                            </td>
                                            <td align="right">
                                                {profile?.logo?.url && (
                                                    <img src={profile.logo.url} alt="logo" width="60" />
                                                )}
                                                <h5 style={{ margin: 0 }}>{profile?.businessName}</h5>
                                                <p style={{ margin: 0 }}>{profile?.address}</p>
                                                <p style={{ margin: 0 }}>GST: {profile?.gstNumber}</p>
                                                <p style={{ margin: 0 }}>
                                                    {profile?.email}, {profile?.contactNumber}
                                                </p>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>

                        {/* ================= CUSTOMER + INVOICE INFO ================= */}
                        <tr>
                            <td style={{ padding: "20px" }}>
                                <table width="100%">
                                    <tbody>
                                        <tr>
                                            <td width="50%">
                                                <strong>Issued For:</strong>
                                                <table>
                                                    <tbody>
                                                        <tr><td>Name:</td><td>{bill?.customerName}</td></tr>
                                                        <tr><td>Address:</td><td>{bill?.customerAddress}</td></tr>
                                                        <tr><td>Phone:</td><td>{bill?.customerMobile}</td></tr>
                                                    </tbody>
                                                </table>
                                            </td>
                                            <td width="50%" align="right">
                                                <table>
                                                    <tbody>
                                                        <tr>
                                                            <td>Invoice Date:</td>
                                                            <td>{moment(bill?.createdAt).format("DD/MM/YYYY")}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Invoice No:</td>
                                                            <td>#{bill?.invoiceNumber}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>

                        {/* ================= ITEMS TABLE ================= */}
                        <tr>
                            <td style={{ padding: "20px" }}>
                                <table width="100%" border="1" cellPadding="8" cellSpacing="0">
                                    <thead>
                                        <tr>
                                            <th>SL</th>
                                            <th align="left">Item</th>
                                            <th>Qty</th>
                                            <th>Rate</th>
                                            <th align="right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bill?.items?.map((item, index) => (
                                            <tr key={item._id}>
                                                <td align="center">{index + 1}</td>
                                                <td>{item.name}</td>
                                                <td align="center">{item.quantity}</td>
                                                <td align="center">₹{item.price}</td>
                                                <td align="right">₹{item.total}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </td>
                        </tr>

                        {/* ================= TOTALS ================= */}
                        <tr>
                            <td style={{ padding: "20px" }}>
                                <table width="100%">
                                    <tbody>
                                        <tr>
                                            <td>
                                                <p><strong>Payment Mode:</strong> {bill?.paymentMode}</p>
                                                <p>Thanks for your business</p>
                                            </td>
                                            <td align="right">
                                                <table>
                                                    <tbody>
                                                        <tr>
                                                            <td>Subtotal:</td>
                                                            <td align="right">₹{bill?.subTotal}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Tax:</td>
                                                            <td align="right">₹{bill?.tax || 0}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Total:</strong></td>
                                                            <td align="right"><strong>₹{bill?.grandTotal}</strong></td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>

                        {/* ================= FOOTER ================= */}
                        <tr>
                            <td style={{ padding: "40px 20px", textAlign: "center" }}>
                                <p><strong>Thank You!</strong></p>
                                <table width="100%" style={{ marginTop: "40px" }}>
                                    <tbody>
                                        <tr>
                                            <td align="left">_____________________<br />Customer Signature</td>
                                            <td align="right">_____________________<br />Authorized Signature</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>

                    </tbody>
                </table>

            </div>

        </div>
    );
};

export default BillInvoicePreview;
